import { useState } from 'react'
import { useEditorState } from '@tiptap/react'

import AlignCenterOutlined from '@ant-design/icons/AlignCenterOutlined'
import AlignLeftOutlined from '@ant-design/icons/AlignLeftOutlined'
import AlignRightOutlined from '@ant-design/icons/AlignRightOutlined'
import BoldOutlined from '@ant-design/icons/BoldOutlined'
import CheckSquareOutlined from '@ant-design/icons/CheckSquareOutlined'
import CodeOutlined from '@ant-design/icons/CodeOutlined'
import DownOutlined from '@ant-design/icons/DownOutlined'
import FileImageOutlined from '@ant-design/icons/FileImageOutlined'
import HighlightOutlined from '@ant-design/icons/HighlightOutlined'
import ItalicOutlined from '@ant-design/icons/ItalicOutlined'
import LinkOutlined from '@ant-design/icons/LinkOutlined'
import MessageOutlined from '@ant-design/icons/MessageOutlined'
import OrderedListOutlined from '@ant-design/icons/OrderedListOutlined'
import PlayCircleOutlined from '@ant-design/icons/PlayCircleOutlined'
import RedoOutlined from '@ant-design/icons/RedoOutlined'
import StrikethroughOutlined from '@ant-design/icons/StrikethroughOutlined'
import UnderlineOutlined from '@ant-design/icons/UnderlineOutlined'
import UndoOutlined from '@ant-design/icons/UndoOutlined'
import UnorderedListOutlined from '@ant-design/icons/UnorderedListOutlined'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import FormHelperText from '@mui/material/FormHelperText'
import IconButton from '@mui/material/IconButton'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Popover from '@mui/material/Popover'
import Stack from '@mui/material/Stack'
import { alpha } from '@mui/material/styles'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'

import { detectVideo } from './extensions/Video'

export default function Toolbar({ editor }) {
  const state = useEditorState({
    editor,
    selector: ({ editor }) => ({
      blockLabel: (BLOCK_OPTIONS.find((o) => o.active(editor)) || BLOCK_OPTIONS[0]).label,
      canUndo: editor.can().undo(),
      canRedo: editor.can().redo(),
      isBold: editor.isActive('bold'),
      isItalic: editor.isActive('italic'),
      isUnderline: editor.isActive('underline'),
      isStrike: editor.isActive('strike'),
      isHighlight: editor.isActive('highlight'),
      isBulletList: editor.isActive('bulletList'),
      isOrderedList: editor.isActive('orderedList'),
      isBlockquote: editor.isActive('blockquote'),
      isCodeBlock: editor.isActive('codeBlock'),
      isAlignLeft: editor.isActive({ textAlign: 'left' }),
      isAlignCenter: editor.isActive({ textAlign: 'center' }),
      isAlignRight: editor.isActive({ textAlign: 'right' }),
    }),
  })

  return (
    <Box sx={[styles.toolbar, surface]}>
      <Group>
        <Icon title="Undo" disabled={!state.canUndo} onClick={() => editor.chain().focus().undo().run()}>
          <UndoOutlined />
        </Icon>
        <Icon title="Redo" disabled={!state.canRedo} onClick={() => editor.chain().focus().redo().run()}>
          <RedoOutlined />
        </Icon>
      </Group>

      <BlockSelect editor={editor} label={state.blockLabel} />

      <Group>
        <Icon title="Bold" isActive={state.isBold} onClick={() => editor.chain().focus().toggleBold().run()}>
          <BoldOutlined />
        </Icon>
        <Icon title="Italic" isActive={state.isItalic} onClick={() => editor.chain().focus().toggleItalic().run()}>
          <ItalicOutlined />
        </Icon>
        <Icon title="Underline" isActive={state.isUnderline} onClick={() => editor.chain().focus().toggleUnderline().run()}>
          <UnderlineOutlined />
        </Icon>
        <Icon title="Strikethrough" isActive={state.isStrike} onClick={() => editor.chain().focus().toggleStrike().run()}>
          <StrikethroughOutlined />
        </Icon>
        <Icon title="Highlight" isActive={state.isHighlight} onClick={() => editor.chain().focus().toggleHighlight().run()}>
          <HighlightOutlined />
        </Icon>
      </Group>

      <Group>
        <LinkButton editor={editor} />
        <Icon title="Bullet list" isActive={state.isBulletList} onClick={() => editor.chain().focus().toggleBulletList().run()}>
          <UnorderedListOutlined />
        </Icon>
        <Icon title="Numbered list" isActive={state.isOrderedList} onClick={() => editor.chain().focus().toggleOrderedList().run()}>
          <OrderedListOutlined />
        </Icon>
      </Group>

      <Group>
        <Icon title="Quote" isActive={state.isBlockquote} onClick={() => editor.chain().focus().toggleBlockquote().run()}>
          <MessageOutlined />
        </Icon>
        <Icon title="Code block" isActive={state.isCodeBlock} onClick={() => editor.chain().focus().toggleCodeBlock().run()}>
          <CodeOutlined />
        </Icon>
      </Group>

      <Group>
        <Icon title="Align left" isActive={state.isAlignLeft} onClick={() => editor.chain().focus().setTextAlign('left').run()}>
          <AlignLeftOutlined />
        </Icon>
        <Icon title="Align center" isActive={state.isAlignCenter} onClick={() => editor.chain().focus().setTextAlign('center').run()}>
          <AlignCenterOutlined />
        </Icon>
        <Icon title="Align right" isActive={state.isAlignRight} onClick={() => editor.chain().focus().setTextAlign('right').run()}>
          <AlignRightOutlined />
        </Icon>
      </Group>

      <Group>
        <ImageButton editor={editor} />
        <VideoButton editor={editor} />
      </Group>
    </Box>
  )
}

const Group = ({ children }) => <Box sx={styles.group}>{children}</Box>

export const Icon = ({ children, isActive = false, ...rest }) => (
  <IconButton size="small" color="secondary" sx={isActive && styles.activeBtn} {...rest}>
    {children}
  </IconButton>
)

function BlockSelect({ editor, label }) {
  const [anchorEl, setAnchorEl] = useState(null)
  return (
    <>
      <Button size="small" variant="text" onClick={(e) => setAnchorEl(e.currentTarget)} endIcon={<DownOutlined />} sx={styles.blockButton}>
        {label}
      </Button>
      <Menu open={Boolean(anchorEl)} anchorEl={anchorEl} onClose={() => setAnchorEl(null)}>
        {BLOCK_OPTIONS.map((o) => (
          <MenuItem
            key={o.label}
            selected={o.active(editor)}
            onClick={() => {
              o.run(editor)
              setAnchorEl(null)
            }}
          >
            {o.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  )
}

export function LinkButton({ editor }) {
  const [anchorEl, setAnchorEl] = useState(null)
  const [href, setHref] = useState('')

  const open = (e) => {
    setHref(editor.getAttributes('link').href || '')
    setAnchorEl(e.currentTarget)
  }
  const confirm = () => {
    const chain = editor.chain().focus().extendMarkRange('link')
    if (href) chain.setLink({ href }).run()
    else chain.unsetLink().run()
    setAnchorEl(null)
    setHref('')
  }

  const isActive = useEditorState({ editor, selector: ({ editor }) => editor.isActive('link') })

  return (
    <>
      <Icon title="Add or edit link" isActive={isActive} onClick={open}>
        <LinkOutlined />
      </Icon>
      <Popover open={Boolean(anchorEl)} anchorEl={anchorEl} onClose={() => setAnchorEl(null)}>
        <Box sx={{ p: 2, width: 360 }}>
          <Stack spacing={1}>
            <TextField
              fullWidth
              autoFocus
              size="small"
              label="Link URL"
              value={href}
              onChange={(e) => setHref(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  confirm()
                }
              }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
              <Button onClick={() => setAnchorEl(null)}>Cancel</Button>
              <Button variant="contained" onClick={confirm}>
                OK
              </Button>
            </Box>
          </Stack>
        </Box>
      </Popover>
    </>
  )
}

function ImageButton({ editor }) {
  const onPick = (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = () => editor.chain().focus().setImage({ src: reader.result }).run()
    reader.readAsDataURL(file)
    e.target.value = ''
  }
  return (
    <IconButton size="small" title="Insert image" color="secondary" variant="text" component="label">
      <FileImageOutlined />
      <input type="file" accept="image/*" hidden onChange={onPick} />
    </IconButton>
  )
}

function VideoButton({ editor }) {
  const [anchorEl, setAnchorEl] = useState(null)
  const [url, setUrl] = useState('')
  const [error, setError] = useState('')

  const detected = detectVideo(url)

  const close = () => {
    setAnchorEl(null)
    setUrl('')
    setError('')
  }

  const handleEmbed = () => {
    if (!detected) {
      setError('Please enter a valid YouTube or Vimeo URL.')
      return
    }
    editor.chain().focus().insertVideo({ provider: detected.provider, videoId: detected.id }).run()
    close()
  }

  return (
    <>
      <IconButton
        size="small"
        title="Insert Video (YouTube or Vimeo)"
        color="secondary"
        variant="text"
        onClick={(e) => setAnchorEl(e.currentTarget)}
      >
        <PlayCircleOutlined />
      </IconButton>
      <Popover open={Boolean(anchorEl)} anchorEl={anchorEl} onClose={close} anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}>
        <Box sx={{ p: 2, width: 360 }}>
          <Stack spacing={1}>
            <Typography variant="subtitle2">Embed a video</Typography>
            <TextField
              fullWidth
              autoFocus
              size="small"
              placeholder="Paste a YouTube or Vimeo URL"
              value={url}
              onChange={(e) => {
                setUrl(e.target.value)
                setError('')
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  handleEmbed()
                }
              }}
              slotProps={{
                input: {
                  endAdornment: detected && (
                    <Chip size="small" color="success" label={detected.provider === 'vimeo' ? 'Vimeo' : 'YouTube'} />
                  ),
                },
              }}
            />
            <Typography variant="caption" color="text.secondary">
              youtube.com/watch?v=… · youtu.be/… · vimeo.com/…
            </Typography>
            {error && <FormHelperText error>{error}</FormHelperText>}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
              <Button onClick={close}>Cancel</Button>
              <Button variant="contained" onClick={handleEmbed}>
                Embed
              </Button>
            </Box>
          </Stack>
        </Box>
      </Popover>
    </>
  )
}

const BLOCK_OPTIONS = [
  { label: 'Normal text', run: (e) => e.chain().focus().setParagraph().run(), active: (e) => e.isActive('paragraph') },
  {
    label: 'Heading 1',
    run: (e) => e.chain().focus().toggleHeading({ level: 1 }).run(),
    active: (e) => e.isActive('heading', { level: 1 }),
  },
  {
    label: 'Heading 2',
    run: (e) => e.chain().focus().toggleHeading({ level: 2 }).run(),
    active: (e) => e.isActive('heading', { level: 2 }),
  },
  {
    label: 'Heading 3',
    run: (e) => e.chain().focus().toggleHeading({ level: 3 }).run(),
    active: (e) => e.isActive('heading', { level: 3 }),
  },
]

export const surface = (theme) => ({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: 1,
  borderRadius: 1,
  backdropFilter: `blur(${theme.spacing(1)})`,
  WebkitBackdropFilter: `blur(${theme.spacing(1)})`,
  boxShadow: theme.customShadows.z2,
  backgroundColor: alpha(theme.palette.background.paper, 0.7),
})

const styles = {
  toolbar: {
    position: 'sticky',
    top: (theme) => theme.spacing(1),
    zIndex: 5,
    p: 1,
    my: 1,
    mx: -2,
  },
  group: {
    display: 'flex',
    alignItems: 'center',
    gap: 0.5,
    backgroundColor: 'grey.200',
    borderRadius: 1,
    p: 0.5,
  },
  activeBtn: {
    backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.16),
  },
  blockButton: {
    backgroundColor: 'grey.200',
    color: 'text.primary',
    textTransform: 'none',
    fontWeight: 600,
    borderRadius: 1,
    px: 1,
    height: 38,
    '&:hover': { backgroundColor: 'grey.400' },
  },
}
